import AddItemDialog, {
  AddItemType,
} from "@/components/organism/AddItemDialog";
import { ReactComponent as AddSvg } from "@/src/assets/icons/icon-add-black.svg";
import AddBlueSvg from "@/src/assets/icons/icon-add-blue.svg";
import ChevronLeftSvg from "@/src/assets/icons/icon-chevron-left-black.svg";
import InfoSvg from "@/src/assets/icons/icon-info.svg";
import PreviewSvg from "@/src/assets/icons/icon-preview-black.svg";
import AlertDialog from "@/src/components/atom/AlertDialog";
import { Button } from "@/src/components/atom/Button";
import SelectOptionButton from "@/src/components/atom/Button/SelectOptionButton";
import Icon from "@/src/components/atom/Icon";
import Input from "@/src/components/atom/Input";
import Loader from "@/src/components/atom/Loader";
import Select from "@/src/components/atom/Select";
import Typo from "@/src/components/atom/Typo";
import BottomFixedContainer from "@/src/components/molecule/BottomFixedContainer";
import BreakWordTypo from "@/src/components/molecule/BreakWordTypo";
import Flex from "@/src/components/molecule/Flex";
import FormItem, { InputError } from "@/src/components/molecule/FormItem";
import SectionCard from "@/src/components/molecule/SectionCard";
import SectionCardGrid from "@/src/components/molecule/SectionCardGrid";
import SectionCardRow from "@/src/components/molecule/SectionCardRow";
import NoRegisteredSignatureAlertDialog from "@/src/components/organism/NoRegisteredSignatureAlertDialog";
import ReferenceImageDialog from "@/src/components/organism/ReferenceImageDialog";
import RegisterCompanySignDialog from "@/src/components/organism/RegisterCompanySignDialog";
import SignatureRequestAlertDialog from "@/src/components/organism/SignatureRequestAlertDialog";
import TermsAddDialog from "@/src/components/organism/TermsAddDialog";
import ImporterMainLayout from "@/src/components/template/Layout/importer/ImporterMainLayout";
import { DIRECT_INPUT_LABEL, DIRECT_INPUT_VALUE } from "@/src/constant";
import DATE_FORMAT_STRINGS from "@/src/constant/dateFormat";
import useAlert from "@/src/hooks/useAlert";
import useContentLoading from "@/src/hooks/useContentLoading";
import IMPORTER_PRIVATE_PATH from "@/src/routes/importer/path";
import { useGetSessionQuery } from "@/src/store/apis/auth";
import {
  useCreateBuyerEmployeesMutation,
  useGetBuyersQuery,
  useLazyGetBuyerEmployeesQuery,
} from "@/src/store/apis/client/buyer";
import { GenerateBuyerEmployeeDto } from "@/src/store/apis/client/buyer/interface";
import { useGetCommonCodeListQuery } from "@/src/store/apis/common";
import {
  useCreateContractBasedScMutation,
  useLazyCheckPoNoQuery,
} from "@/src/store/apis/contracts";
import { useLazyGetImporterContractDetailQuery } from "@/src/store/apis/contracts/contractDetail";
import { useGetExportersQuery } from "@/src/store/apis/corporate/company";
import {
  useCreateSingleItemCodeMutation,
  useGetNonFilteredItemCodesQuery,
} from "@/src/store/apis/corporate/companyItemManagement";
import { ExporterItemCodeListViewV2Dto } from "@/src/store/apis/corporate/companyItemManagement/interface";
import colorSet from "@/src/styles/color";
import { mediaQuery } from "@/src/styles/mediaQuery";
import typo from "@/src/styles/typography";
import { commonCodeMapper } from "@/src/utils/commonCodeHelper";
import { isNull, isUndefined } from "@/src/utils/is";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled, { css } from "styled-components";
import { GroupType, OptionType } from "types";
import BuyerEmployeeAddDialog from "../../../ClientManagement/dialog/BuyerEmployeeAddDialog";
import { ContractAddFormBasedOnScType } from "../../types/contract.type";
import { contractAddBasedOnScForm } from "../../utils/defaultValue";
import SignatureSelectDialog from "@/src/components/organism/SignatureSelectDialog";
import { PoData } from "@/src/components/template/pdfs/Po/types";
import { poPreview } from "@/src/components/template/pdfs/Po/PoContent";
import DatePickerNoticeTypo from "@/src/components/molecule/DatePickerNoticeTypo";

enum DialogState {
  NULL,
  IMAGE,
  LOAD_CONTRACT,
  ADD_ITEM,
  ADD_CONTACT_PERSON,
  CREATE_TERMS,
  REGISTER_SIGN,
}

enum AlertDialogState {
  NULL,
  SAVED_DATA,
  BACK_TO_PREVIOUS,
  REGISTER_SIGN,
  SIGNATURE_REQUEST,
  SIGNATURE_SELECT_PO,
}

const EMPTY_ARRAY: [] = [];

interface ContractAddBasedOnScPageProps {
  id: number;
}

const ContractAddBasedOnScPage = ({ id }: ContractAddBasedOnScPageProps) => {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const alert = useAlert();
  const { handleContentLoadingOn, handleContentLoadingOff } =
    useContentLoading();

  const [selectImage, setSelectImage] = useState("");
  const [dialogState, setDialogState] = useState<DialogState>(DialogState.NULL);
  const [alertDialogState, setAlertDialogState] = useState<AlertDialogState>(
    AlertDialogState.NULL
  );

  const exportersData = useGetExportersQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const isCompanyTerms =
    !!exportersData.currentData?.termsOfSalesContract
      .salesContractTermsWithLatestHistory;

  const isTwoSign =
    exportersData.currentData?.signatureManagement
      ?.simpleExporterSignatureMedias?.length === 2;

  const defaultSignatureMediaId =
    exportersData?.currentData?.signatureManagement?.simpleExporterSignatureMedias?.find(
      (item) => item.isDefault
    )?.signatureSimpleMedia.id as number;

  const {
    control,
    register,
    watch,
    setValue,
    reset,
    formState: { errors },
    handleSubmit,
    getValues,
    clearErrors,
  } = useForm<ContractAddFormBasedOnScType>({
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      ...contractAddBasedOnScForm,
      isAddContractTerms: isCompanyTerms ? ["true"] : [],
    },
  });

  const [
    getContractDetail,
    {
      quantityAndUnit,
      unitPriceAndUnit,
      amount,
      currentData,
      isOriginByExport,
      isFreetimeByExport,
      isLastShipmentDateByExport,
      sellerMainCategory,
    },
  ] = useLazyGetImporterContractDetailQuery({
    selectFromResult: ({ currentData, isError, isFetching }) => {
      const isUnstable = isUndefined(currentData) || isError || isFetching;

      return {
        quantityAndUnit:
          !isUnstable && currentData.quantity && currentData.quantityUnit
            ? `${Number(currentData.quantity).toLocaleString("ko-KR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })} ${currentData.quantityUnit}`
            : "-",
        unitPriceAndUnit:
          !isUnstable && currentData.unitPrice && currentData.unitPriceUnit
            ? `${Number(currentData.unitPrice).toLocaleString("ko-KR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })} ${currentData.unitPriceUnit}`
            : "-",
        amount:
          !isUnstable &&
          currentData.quantity &&
          currentData.unitPrice &&
          currentData.unitPriceUnit
            ? `${parseFloat(
                (
                  Number(currentData.quantity) * Number(currentData.unitPrice)
                ).toFixed(2)
              ).toLocaleString("ko-KR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })} ${currentData.unitPriceUnit}`
            : "-",
        isFreetimeByExport: !isUnstable ? !!currentData?.freetime : false,
        isOriginByExport: !isUnstable ? !!currentData?.origin : false,
        isLastShipmentDateByExport: !isUnstable
          ? !!currentData?.lastShipmentDateAt
          : false,
        currentData,
        sellerMainCategory: !isUnstable ? currentData.mainCategory : null,
      };
    },
  });

  // SC PDF
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const previewButtonRef = useRef<HTMLButtonElement>(null);
  const previewPoPdfResourceRef = useRef<ContractAddFormBasedOnScType | null>(
    null
  );
  const { mainCategoryList, exporterUserType } = useGetSessionQuery(undefined, {
    refetchOnMountOrArgChange: true,
    selectFromResult: ({ currentData, isError, isFetching }) => {
      const isErrorAndUndefined =
        isError || isFetching || currentData === undefined;

      const isManager =
        currentData?.row.exporterUserType === "MANAGER" ||
        currentData?.row.exporterUserType === "MIDDLE_MANAGER";

      return {
        mainCategoryList: !isErrorAndUndefined
          ? isManager
            ? currentData.row.mainCategoryCodeItemNames
            : currentData.row.exporter.mainCategoryCodeItemNames
          : EMPTY_ARRAY,
        exporterUserType: currentData?.row.exporterUserType,
      };
    },
  });
  /**
   * RTK Query상 Create지만 실제 데이터상으로는 SC기반의 PO수정
   */
  const [createContract] = useCreateContractBasedScMutation();
  const [createEmployee] = useCreateBuyerEmployeesMutation();
  const [checkPoNo] = useLazyCheckPoNoQuery();

  const { buyerList, isBuyersFetching } = useGetBuyersQuery(
    {},
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ currentData, isFetching, isError }) => {
        const isUnstable = isUndefined(currentData) || isFetching || isError;

        return {
          buyerList: !isUnstable ? currentData.data : EMPTY_ARRAY,
          isBuyersFetching: isFetching,
        };
      },
    }
  );
  const [createSingleItemCode] = useCreateSingleItemCodeMutation();
  const [getBuyerEmployees, buyerEmployeesResult] =
    useLazyGetBuyerEmployeesQuery();
  const {
    itemsList,
    isItemsFetching,
    refetch: itemListRefetch,
  } = useGetNonFilteredItemCodesQuery(
    {},
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ currentData, isFetching, isError }) => {
        const isUnstable = isUndefined(currentData) || isFetching || isError;
        const isStable = !isUnstable;

        return {
          itemsList: isStable ? currentData.rows : EMPTY_ARRAY,
          isItemsFetching: isFetching,
        };
      },
    }
  );
  const { isCommonCodeFetching, commonCodeCurrentData } =
    useGetCommonCodeListQuery(
      {
        codeNameList: [{ codeName: "FREETIME" }, { codeName: "MAIN_CATEGORY" }],
      },
      {
        refetchOnMountOrArgChange: true,
        selectFromResult: ({ isFetching, currentData }) => {
          return {
            isCommonCodeFetching: isFetching,
            commonCodeCurrentData: currentData ?? EMPTY_ARRAY,
          };
        },
      }
    );

  const [freeTimeList = [], commonMainCategoryList = []] =
    commonCodeCurrentData;

  const buyerToOptionList = buyerList
    .filter(
      ({ isActivated, mainCategoryCodeItemNames }) =>
        isActivated &&
        mainCategoryCodeItemNames.some((mainCategory) =>
          mainCategoryList.includes(mainCategory)
        )
    )
    .reduce<OptionType[]>((acc, val) => {
      const resource = {
        label: val.buyerName,
        value: val.buyerListQueryResultDto.id.toString(),
      };
      return [...acc, resource];
    }, []);

  const buyerListToOptionList = buyerEmployeesResult.isError
    ? []
    : buyerEmployeesResult.currentData?.rows
        .filter(({ isActivated }) => isActivated)
        .reduce<OptionType[]>((acc, val) => {
          const resource = {
            label: val.name,
            value: val.id.toString(),
          };
          return [...acc, resource];
        }, []);

  const buyerMainCategory =
    buyerList.find(({ id }) => currentData?.importerBuyerId === id)
      ?.mainCategoryCodeItemNames ?? [];

  const itemListToGroupedOptionList = itemsList
    .filter(({ isActivated, mainCategoryCodeItemName, mainCategory }) => {
      return (
        isActivated &&
        mainCategoryList.includes(mainCategoryCodeItemName) &&
        buyerMainCategory.includes(mainCategoryCodeItemName) &&
        mainCategory === sellerMainCategory
      );
    })
    .reduce((acc: GroupType[], item: ExporterItemCodeListViewV2Dto) => {
      const groupLabel = `${item.mainCategory} > ${item.subCategory}`;
      // 그룹이 이미 존재하는지 확인
      let group = acc.find((g) => g.label === groupLabel);
      if (!group) {
        group = { label: groupLabel, options: [] };
        acc.push(group);
      }
      // 옵션 레이블 생성
      const isItemCodeSameAsItem = item.item === item.itemCode;
      const optionLabel = isItemCodeSameAsItem
        ? `${item.item}`
        : `${item.item}(${item.itemCode})`;
      // value 필드 수정
      const optionValue = `${item.id}_${item.exporterItemId}`;
      // 옵션 추가
      group.options.push({
        label: optionLabel,
        value: optionValue,
      });

      return acc;
    }, []);

  const freetimeOptions = freeTimeList.reduce<OptionType[]>(
    commonCodeMapper,
    []
  );

  const referenceImageList =
    getValues("referenceMediaIdList")?.map((item) => item.mediaUrl) || [];

  const selectBuyer = buyerEmployeesResult
    ? buyerEmployeesResult?.currentData?.rows.filter(
        (item) => item.id === Number(watch("importerBuyerEmployeeId"))
      )[0]?.buyer
    : null;

  const handlePreview = () => {
    const poData: PoData = {
      poNumber: previewPoPdfResourceRef.current?.poNo || "",
      componyLogoUrl:
        exportersData.currentData?.basicInformation?.businessLogoSimpleMedia
          ?.mediaUrl || "",

      sellerCorporation: selectBuyer ? selectBuyer?.buyerName : "",
      sellerContactPrefix: selectBuyer ? selectBuyer?.telPrefix : "",
      sellerContact: selectBuyer ? selectBuyer?.tel : "",
      sellerFaxPrefix: selectBuyer ? selectBuyer?.faxPrefix : "",
      sellerFax: selectBuyer ? selectBuyer?.fax : "",
      sellerCountryName: selectBuyer ? selectBuyer?.buyerCountryName : "",
      sellerRegion: selectBuyer ? selectBuyer?.buyerRegion : "",
      sellerPostalCode: selectBuyer ? selectBuyer?.buyerPostalCode : "",
      sellerLocality: selectBuyer ? selectBuyer?.buyerLocality : "",
      sellerStreetAddress: selectBuyer
        ? selectBuyer?.buyerStreetAddress || ""
        : "",
      sellerContactPersonName:
        buyerListToOptionList?.find(
          (employee) =>
            employee.value ===
            previewPoPdfResourceRef.current?.importerBuyerEmployeeId
        )?.label ?? "",

      buyerCorporation:
        exportersData.currentData?.basicInformation?.companyName || "",
      buyerContactPrefix:
        exportersData.currentData?.exporterDetailQueryResultDto?.telPrefix ||
        "",
      buyerContact:
        exportersData.currentData?.exporterDetailQueryResultDto?.tel || "",
      buyerFaxPrefix:
        exportersData?.currentData?.exporterDetailQueryResultDto?.faxPrefix ||
        "",
      buyerFax:
        exportersData?.currentData?.exporterDetailQueryResultDto?.fax || "",
      buyerCountryName:
        exportersData?.currentData?.exporterDetailQueryResultDto?.countryName ||
        "",
      buyerRegion:
        exportersData?.currentData?.exporterDetailQueryResultDto?.region || "",
      buyerPostalCode:
        exportersData?.currentData?.exporterDetailQueryResultDto?.postalCode ||
        "",
      buyerLocality:
        exportersData?.currentData?.exporterDetailQueryResultDto?.locality ||
        "",
      buyerStreetAddress:
        exportersData?.currentData?.exporterDetailQueryResultDto
          ?.streetAddress || "",

      orderDate: previewPoPdfResourceRef.current?.importerOrderDateAt
        ? dayjs(previewPoPdfResourceRef.current?.importerOrderDateAt).format(
            DATE_FORMAT_STRINGS.YYYY_MM_DD
          )
        : "",
      paymentTerm: currentData?.paymentTerms || "",
      paymentTermsRemark: currentData?.paymentTermsRemark || "",
      originLocation:
        previewPoPdfResourceRef.current?.origin || currentData?.origin || "",
      shippingTerm: currentData?.shippingTerms || "",
      shippingTermRemark: currentData?.shippingTermsRemark || "",
      lastShipmentDate:
        dayjs(
          currentData?.lastShipmentDateAt
            ? currentData?.lastShipmentDateAt
            : previewPoPdfResourceRef.current?.lastShipmentDateAt
        ).format(DATE_FORMAT_STRINGS.YYYY_MM_DD) || "",
      deliveryDate: previewPoPdfResourceRef.current?.deliveryDateAt
        ? dayjs(previewPoPdfResourceRef.current?.deliveryDateAt).format(
            DATE_FORMAT_STRINGS.YYYY_MM_DD
          )
        : "",
      quantityPrefix: currentData?.quantityUnit || "",
      quantity: currentData?.quantity
        ? Number(
            currentData?.quantity.toString().replace(/[,]/g, "")
          ).toLocaleString("ko-KR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : "",
      description: previewPoPdfResourceRef.current?.item || "",
      hsCode: previewPoPdfResourceRef.current?.hsCode || "",
      unitPricePrefix: currentData?.unitPriceUnit || "",
      unitPrice: currentData?.unitPrice
        ? Number(
            currentData?.unitPrice.toString().replace(/[,]/g, "")
          ).toLocaleString("ko-KR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : "",
      amount:
        (currentData?.quantity &&
          currentData?.unitPrice &&
          Number(
            (
              (parseFloat(currentData?.quantity.toString().replace(/,/g, "")) ||
                0) *
              (parseFloat(
                currentData?.unitPrice.toString().replace(/,/g, "")
              ) || 0)
            ).toFixed(2)
          ).toLocaleString("ko-KR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })) ||
        "",
      remark: previewPoPdfResourceRef.current?.importerContractRemark || "",
      freeTime:
        currentData?.freetime ||
        (previewPoPdfResourceRef.current?.freetimeCodeItemName ===
        DIRECT_INPUT_VALUE
          ? previewPoPdfResourceRef.current.freetimeDirect
          : (previewPoPdfResourceRef.current?.freetime ?? "")),
      placeOfDelivery: currentData?.placeOfDelivery || "",
      // !!previewPoPdfResourceRef.current?.isAddContractTerms.length,
      isAddContractTerms: false,
      contractTermsTitle: "",
      contractTerms: "",

      sellerSignatureUrl: "",
      buyerSignatureUrl:
        exportersData?.currentData?.signatureManagement?.simpleExporterSignatureMedias?.find(
          (item) => item.isDefault
        )?.signatureSimpleMedia.mediaUrl || "",
    };

    poPreview(poData);
  };

  const handleImageClick = (image: string) => {
    setDialogState(DialogState.IMAGE);
    setSelectImage(image);
  };

  const handleCreateContract = async () => {
    /**
     * - 기업관리자, 중간관리자 서명등록
     * - 일반 매니저 기업관리자에게 서명요청
     */
    if (
      isNull(
        exportersData.currentData?.signatureManagement
          .simpleExporterSignatureMedias
      )
    ) {
      if (exporterUserType !== "MANAGER") {
        return setAlertDialogState(AlertDialogState.REGISTER_SIGN);
      } else {
        return setAlertDialogState(AlertDialogState.SIGNATURE_REQUEST);
      }
    }
    /**
     * - 서명이 2개일 경우 서명선택모달
     * - 서명이 1개일 경우 default
     */
    isTwoSign
      ? setAlertDialogState(AlertDialogState.SIGNATURE_SELECT_PO)
      : createPoContract(getValues(), defaultSignatureMediaId);
  };

  const createPoContract = async (
    formValue: ContractAddFormBasedOnScType,
    selectSignatureMediaId?: number
  ) => {
    try {
      handleContentLoadingOn();
      
      const { row } = await createContract({
        /**
         * Basic Information
         */
        id,
        poNo: formValue.poNo,
        importerBuyerId: Number(formValue.importerBuyerId),
        importerBuyerEmployeeId: Number(formValue.importerBuyerEmployeeId),
        importerOrderDateAt: dayjs(formValue.importerOrderDateAt)
          .startOf("day")
          .toISOString(),
        isAddContractTerms: getValues("isAddContractTerms")
          ? !!getValues("isAddContractTerms").length
          : false,
        hsCode: formValue.hsCode || undefined,
        importerItemId: Number(formValue.importerItemId.split("_")[1]),
        importerItemCodeId: Number(formValue.importerItemCodeId),
        /**
         * Transaction Information
         */
        freetime:
          formValue.freetimeCodeItemName === DIRECT_INPUT_VALUE
            ? formValue.freetimeDirect || undefined
            : formValue.freetime || undefined,
        freetimeCodeItemName:
          formValue.freetimeCodeItemName === DIRECT_INPUT_VALUE
            ? undefined
            : formValue.freetimeCodeItemName || undefined,
        lastShipmentDateAt: formValue.lastShipmentDateAt
          ? dayjs(formValue.lastShipmentDateAt).startOf("day").toISOString()
          : undefined,
        /**
         * Attachment file
         */
        importerReferenceMediaIdList: formValue.referenceMediaIdList.length
          ? formValue.referenceMediaIdList.map(({ id }) => id)
          : undefined,
        poMediaId: formValue?.poMediaId?.[0]?.id,
        importerContractRemark: formValue.importerContractRemark || undefined,
        deliveryDateAt: dayjs(formValue.deliveryDateAt)
          .startOf("day")
          .toISOString(),

        isSendToBuyer: true,
        signatureMediaId: selectSignatureMediaId
          ? selectSignatureMediaId
          : defaultSignatureMediaId,
      }).unwrap();

      alert.showAlert({
        type: "success",
        message: t("contract:importer.add.alert.saveSuccess"),
      });
      if (row.id) {
        navigate(`${IMPORTER_PRIVATE_PATH.CONTRACT_DETAIL}/${row.id}`);
      }
    } catch (e: any) {
      const message = e?.data?.message
        ? Array.isArray(e.data.message)
          ? e.data.message[0]
          : e.data.message
        : t("alert:unHandled");

      alert.showAlert({ type: "error", message });
    } finally {
      handleContentLoadingOff();
    }
  };

  const handleAddItems = async ({
    item,
    exporterItemCodeList,
    subCategoryCodeItemName,
    mainCategoryCodeItemName,
  }: AddItemType) => {
    const sellerMainCategoryCodeItemName = commonMainCategoryList.find(
      ({ codeItemNameEn }) => codeItemNameEn === sellerMainCategory
    )?.codeItemName;

    if (sellerMainCategoryCodeItemName !== mainCategoryCodeItemName) {
      return alert.showAlert({
        type: "error",
        message: t("alert:exporterContractCategoryMismatch"),
      });
    }

    try {
      const { row } = await createSingleItemCode({
        item: item,
        itemCode: exporterItemCodeList[0].itemCode,
        subCategoryCodeItemName: subCategoryCodeItemName,
        mainCategoryCodeItemName: mainCategoryCodeItemName,
      }).unwrap();

      itemListRefetch();

      const categoryList =
        buyerList.find(({ id }) => currentData?.importerBuyerId === id)
          ?.mainCategoryCodeItemNames ?? [];

      if (categoryList.includes(mainCategoryCodeItemName)) {
        setValue("importerItemId", `${row.id}_${row.exporterItemId}`);
        setValue("mainCategory", row.mainCategory ?? "");
        setValue("subCategory", row.subCategory ?? "");
        setValue("item", row.item ?? "");
        setValue("importerItemCodeId", String(row.id));
        setValue("importerItemCode", row.itemCode);
        clearErrors("importerItemId");
      }

      alert.showAlert({
        type: "success",
        message: t("alert:saveSuccess"),
      });

      setDialogState(DialogState.NULL);
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;
      alert.showAlert({ type: "error", message });
    }
  };

  const handleAddBuyerEmployee = async (
    values: Omit<GenerateBuyerEmployeeDto, "isActivated"> & {
      isActivated: string;
    }
  ) => {
    const importerBuyerId = getValues("importerBuyerId");

    try {
      setDialogState(DialogState.NULL);
      const { row: createdEmployee } = await createEmployee({
        id: Number(importerBuyerId),
        name: values.name,
        email: values.email,
        isActivated: values.isActivated === "Y",
        personalContactPrefix: values.personalContactPrefix || undefined,
        personalContact: values.personalContact || undefined,
        officeContactPrefix: values.officeContactPrefix || undefined,
        officeContact: values.officeContact || undefined,
        buyerEmployeeRemark: values.buyerEmployeeRemark || undefined,
      }).unwrap();

      await getBuyerEmployees({
        id: Number(importerBuyerId),
      });

      setValue("importerBuyerEmployeeId", createdEmployee.id.toString());
      clearErrors("importerBuyerEmployeeId");
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;
      alert.showAlert({ type: "error", message });
    } finally {
    }
  };

  // 아이템 코드
  const renderItemSubInputs = () => {
    return (
      <>
        <SubFormRow>
          <SubFormLabel>{t("common:category")}</SubFormLabel>
          <Controller
            name="mainCategory"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                style={{ width: "100%" }}
                disabled
                placeholder={t("common:mainCategory")}
                value={
                  getValues("mainCategory")
                    ? `${field.value} > ${watch("subCategory")}`
                    : "-"
                }
              />
            )}
          />
        </SubFormRow>
        <SubFormRow>
          <SubFormLabel>{t("common:itemCode")}</SubFormLabel>
          <Controller
            name="importerItemCode"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                style={{ width: "100%" }}
                disabled
                placeholder={
                  getValues("mainCategory") ? "-" : t("common:itemCode")
                }
              />
            )}
          />
        </SubFormRow>
      </>
    );
  };

  const renderContractInfoBasedPo = () => {
    return (
      <>
        <SectionCardGrid>
          {[
            {
              gridColumn: 6,
              title: t("common:seller"),
              value: (
                <BuyerText
                  color="systemBlue2"
                  typoType="h4"
                  onClick={() =>
                    navigate(
                      `${IMPORTER_PRIVATE_PATH.CLIENT_MANAGEMENT_DETAIL}/${currentData?.importerBuyerId}`
                    )
                  }
                >
                  {currentData?.companyName || "-"}
                </BuyerText>
              ),
            },
            {
              gridColumn: 3,
              title: t("common:shippingTerms"),
              value: (
                <BreakWordTypo typoType="h4">
                  {" "}
                  {currentData?.shippingTerms || "-"}{" "}
                  {currentData?.shippingTermsRemark || ""}
                </BreakWordTypo>
              ),
            },
            {
              gridColumn: 3,
              title: t("common:sellerContact"),
              value: <BreakWordTypo typoType="h4">-</BreakWordTypo>,
            },
            {
              isDivider: true,
            },
            {
              gridColumn: 3,
              title: t("common:item"),
              value: (
                <BreakWordTypo typoType="h4">
                  {currentData?.exporterItem || "-"}
                </BreakWordTypo>
              ),
            },
            {
              gridColumn: 3,
              title: t("common:qty"),
              value: (
                <BreakWordTypo typoType="h4">{quantityAndUnit}</BreakWordTypo>
              ),
            },
            {
              gridColumn: 3,
              title: t("common:unitPrice"),
              value: (
                <BreakWordTypo typoType="h4">{unitPriceAndUnit}</BreakWordTypo>
              ),
            },
            {
              gridColumn: 3,
              title: t("common:amount"),
              value: <BreakWordTypo typoType="h4">{amount}</BreakWordTypo>,
            },
          ].map(({ gridColumn, isDivider = false, title, value }) => {
            if (isDivider) {
              return <StyledSectionCardRow data-columns={12} data-divider />;
            }
            return (
              <StyledSectionCardRow
                data-columns={gridColumn}
                label={
                  <Typo color="gray6" typoType="b9m">
                    {title}
                  </Typo>
                }
                direction="vertical"
                value={value}
              />
            );
          })}
        </SectionCardGrid>

        <BottomSectionCard>
          {[
            {
              gridColumn: 4,
              title: t("common:placeOfDelivery"),
              value: currentData?.placeOfDelivery || "-",
            },
            {
              gridColumn: 4,
              title: t("common:portOfDischarge"),
              value: currentData?.portOfDischarge || "-",
            },
            {
              gridColumn: 4,
              title: t("common:originLocation"),
              value: currentData?.origin || "-",
            },
            {
              isDivider: true,
            },
            {
              gridColumn: 4,
              title: t("common:paymentTerms"),
              value: `${currentData?.paymentTerms || "-"} ${
                currentData?.paymentTermsRemark || ""
              }`,
            },
            {
              gridColumn: 4,
              title: t("common:lastShipmentDate"),
              value: currentData?.lastShipmentDateAt
                ? dayjs(currentData.lastShipmentDateAt).format(
                    DATE_FORMAT_STRINGS.YYYY_MM_DD
                  )
                : "-",
            },
            {
              gridColumn: 4,
              title: t("common:freetime"),
              value: currentData?.freetime || "-",
            },
          ].map(({ gridColumn, isDivider = false, title, value }) => {
            if (isDivider) {
              return <StyledSectionCardRow data-columns={12} data-divider />;
            }
            return (
              <StyledSectionCardCenterRow
                data-columns={gridColumn}
                label={
                  <Typo color="gray6" typoType="b9m">
                    {title}
                  </Typo>
                }
                direction="vertical"
                value={
                  <Typo typoType="h4" color="blue2" className="ant-typography">
                    {value}
                  </Typo>
                }
              />
            );
          })}
        </BottomSectionCard>
      </>
    );
  };

  // 추가 입력 정보 Basic Information, Transaction Information
  const renderAdditionalInfoBasedPo = () => {
    // 선택사항
    const optionalItemValue = () => {
      if (exportersData.isFetching) {
        return <Loader />;
      }

      // 약관이 있는경우에만 체크박스
      if (isCompanyTerms) {
        return (
          <StyledFormItem
            type="checkbox"
            control={control as any}
            name="isAddContractTerms"
            options={[
              {
                label: t("contract:importer.add.common.addContractTerm"),
                value: "true",
              },
            ]}
          />
        );
      }

      return (
        <Flex gap={16} alignItems="center" flexWrap="wrap">
          <Typo typoType="b7r" color="gray7">
            {t("common:noRegisteredOrderTerms")}
          </Typo>
          <Button
            buttonGrade="tertiary"
            buttonColor="black"
            buttonSize={32}
            onClick={() => setDialogState(DialogState.CREATE_TERMS)}
          >
            <Flex alignItems="center" gap={4}>
              <AddIcon />
              {t("common:registerOrderTerms")}
            </Flex>
          </Button>
        </Flex>
      );
    };

    return (
      <>
        <SectionCard
          cardTitle={t(
            "contract:importer.add.common.additionalInputInformation"
          )}
          cardContentContainerStyle={{ display: "flex", gap: "24px" }}
        >
          <FlexColumn flexGap={16}>
            {/* 기본 정보 */}
            <Typo typoType="b9m" color="gray7">
              {t("contract:importer.add.common.basicInformation")}
            </Typo>
            <FlexRow>
              <HalfFormItemContainer>
                <StyledFormItem
                  label={t("contract:importer.add.common.poNo")}
                  type={"text"}
                  name={"poNo"}
                  control={control as any}
                  inputProps={{
                    placeholder: t("contract:importer.add.placeholder.poNo"),
                  }}
                  rules={{
                    maxLength: 50,
                    required: true,
                    validate: async (poNo) => {
                      // 초기값과 현재 입력값이 같으면 검증 통과
                      if (
                        searchParams.get("clientCheck") === "false" &&
                        poNo === currentData?.poNo
                      ) {
                        return true;
                      }
                      // 다른 경우에만 중복 체크 실행
                      if (poNo.length >= 50) return true;
                      const checkResult = await checkPoNo({ poNo });
                      return !checkResult.isError;
                    },
                  }}
                  errorsMessage={{
                    required: t("error:required"),
                    validate: t("error:alreadyExisted"),
                    maxLength: t("error:maxLength50"),
                  }}
                />

                <StyledFormItem
                  label={t("contract:importer.add.common.seller")}
                  rules={{
                    required: true,
                  }}
                  type="singleSelect"
                  control={control as any}
                  name="importerBuyerId"
                  inputContainerClassName="selector-size-m"
                  inputProps={{
                    suffixIcon: isBuyersFetching ? <Loader /> : undefined,
                    disabled: true,
                    placeholder: t("contract:importer.add.placeholder.buyer"),
                  }}
                  options={buyerToOptionList}
                  bottomAccessory={
                    <>
                      <SubFormRow>
                        <SubFormLabel>
                          {t("contract:importer.add.common.sellerContact")}
                          <strong>*</strong>
                        </SubFormLabel>
                        <Controller
                          name="importerBuyerEmployeeId"
                          control={control}
                          rules={{ required: true }}
                          render={({ field }) => (
                            <FlexDiv className="selector-size-s">
                              <StyledSelect
                                {...field}
                                value={field.value || undefined}
                                data-invalid={!!errors.importerBuyerEmployeeId}
                                showSearch
                                filterOption={(input, option) =>
                                  ((option?.label as string) ?? "")
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                                }
                                options={buyerListToOptionList}
                                placeholder={t(
                                  "contract:importer.add.placeholder.buyerContact"
                                )}
                                suffixIcon={
                                  buyerEmployeesResult.isFetching ? (
                                    <Loader />
                                  ) : undefined
                                }
                                dropdownRender={(menu) => {
                                  return (
                                    <>
                                      <DirectInputSelectOptionButton
                                        onClick={() => {
                                          setValue(
                                            "importerBuyerEmployeeId",
                                            ""
                                          );
                                          setDialogState(
                                            DialogState.ADD_CONTACT_PERSON
                                          );
                                        }}
                                      >
                                        {t(DIRECT_INPUT_LABEL)}
                                      </DirectInputSelectOptionButton>
                                      {(buyerListToOptionList?.length ?? 0) >
                                        0 && <div>{menu}</div>}
                                    </>
                                  );
                                }}
                              />
                              {errors.importerBuyerEmployeeId && (
                                <StyledInputError
                                  message={t("error:required")}
                                />
                              )}
                            </FlexDiv>
                          )}
                        />
                      </SubFormRow>
                    </>
                  }
                  errorsMessage={{
                    required: t("error:required"),
                    validate: t("error:alreadyExisted"),
                  }}
                />
                <StyledFormItem
                  label={t("contract:importer.add.common.orderDate")}
                  type="date"
                  control={control as any}
                  rules={{
                    required: true,
                  }}
                  name="importerOrderDateAt"
                  errorsMessage={{
                    required: t("error:required"),
                  }}
                  inputProps={{
                    showToday: false,
                    renderExtraFooter: () => (
                      <DatePickerNoticeTypo
                        text={t("common:datePickerFooterOrderDate")}
                      />
                    ),
                  }}
                />
                <SectionCardRow
                  label={t("contract:exporter.add.common.optional")}
                  value={optionalItemValue()}
                />
              </HalfFormItemContainer>
              <HalfFormItemContainer>
                <CustomFormItemRow>
                  <StyledCustomLabel>
                    {t("contract:importer.add.common.item")}
                  </StyledCustomLabel>
                  <DoubleInputContainerWithError style={{ flex: 1 }}>
                    <DoubleInputContainer style={{ width: "100%" }}>
                      <Controller
                        name="importerItemId"
                        rules={{ required: true }}
                        control={control}
                        render={({ field }) => {
                          const isItemFieldDisabled =
                            isItemsFetching || !watch("importerBuyerId");
                          return (
                            <>
                              <StyledSelect
                                {...field}
                                value={field.value || undefined}
                                style={{ flex: 1, width: 1 }}
                                placeholder={t(
                                  "contract:importer.add.placeholder.item"
                                )}
                                filterOption={(input, option) =>
                                  ((option?.label as string) ?? "")
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                                }
                                options={itemListToGroupedOptionList}
                                data-invalid={!!errors.importerItemId}
                                showSearch
                                suffixIcon={
                                  isItemsFetching ? <Loader /> : undefined
                                }
                                disabled={isItemFieldDisabled}
                                onChange={(value) => {
                                  const splitedValue = (value as string).split(
                                    "_"
                                  );
                                  const selectItem = itemsList.find(
                                    (itemInfo) =>
                                      itemInfo.id === Number(splitedValue[0])
                                  );

                                  if (selectItem) {
                                    setValue("importerItemId", value);
                                    setValue(
                                      "mainCategory",
                                      selectItem.mainCategory
                                    );
                                    setValue(
                                      "subCategory",
                                      selectItem.subCategory
                                    );
                                    setValue("item", selectItem.item);
                                    setValue(
                                      "importerItemCodeId",
                                      splitedValue[0]
                                    );
                                    setValue(
                                      "importerItemCode",
                                      selectItem.itemCode
                                    );
                                  }
                                }}
                              />
                              <StyledButton
                                buttonColor="black"
                                buttonGrade="tertiary"
                                buttonSize={40}
                                onClick={() =>
                                  setDialogState(DialogState.ADD_ITEM)
                                }
                                disabled={isItemFieldDisabled}
                              >
                                <AddIcon disabled={isItemFieldDisabled} />{" "}
                                {t("contract:importer.add.common.addItem")}
                              </StyledButton>
                            </>
                          );
                        }}
                      />
                    </DoubleInputContainer>
                    {errors.importerItemId && (
                      <InputError message={t("error:required")} />
                    )}
                    {watch("mainCategory") && renderItemSubInputs()}
                  </DoubleInputContainerWithError>
                </CustomFormItemRow>

                <StyledFormItem
                  label={t("contract:importer.add.common.hsCode")}
                  control={control as any}
                  name="hsCode"
                  type="text"
                  inputProps={{
                    placeholder: t("contract:importer.add.placeholder.hsCode"),
                  }}
                />
              </HalfFormItemContainer>
            </FlexRow>
            <Divider />

            {/* 거래 정보 */}
            <Typo typoType="b9m" color="gray7">
              {t("contract:importer.add.common.transactionInformation")}
            </Typo>
            <FlexRow>
              <HalfFormItemContainer>
                <StyledFormItem
                  label={t("contract:importer.add.common.deliveryDate")}
                  type="date"
                  control={control as any}
                  name="deliveryDateAt"
                  rules={{ required: true }}
                  errorsMessage={{
                    required: t("error:required"),
                  }}
                  inputProps={{
                    allowClear: true,
                    showToday: false,
                    renderExtraFooter: () => (
                      <DatePickerNoticeTypo
                        text={t("common:datePickerFooterDeliveryDate")}
                      />
                    ),
                  }}
                />
                {!isLastShipmentDateByExport && (
                  <StyledFormItem
                    label={t("contract:importer.add.common.lastShipmentDate")}
                    type="date"
                    control={control as any}
                    name="lastShipmentDateAt"
                    inputProps={{
                      allowClear: true,
                      showToday: false,
                      renderExtraFooter: () => (
                        <DatePickerNoticeTypo
                          text={t("common:datePickerFooterLastShipmentDate")}
                        />
                      ),
                    }}
                  />
                )}
                {!isFreetimeByExport && (
                  <StyledFormItem
                    label={t("contract:importer.add.common.freeTime")}
                    type="singleSelect"
                    name="freetimeCodeItemName"
                    control={control as any}
                    options={[
                      {
                        label: t(DIRECT_INPUT_LABEL),
                        value: DIRECT_INPUT_VALUE,
                      },
                      ...freetimeOptions,
                    ]}
                    inputContainerClassName="selector-size-m"
                    inputProps={{
                      placeholder: t(
                        "contract:importer.add.placeholder.freetime"
                      ),
                      virtual: false,
                      allowClear: true,
                      suffixIcon: isCommonCodeFetching ? <Loader /> : undefined,
                      filterOption: (input, option) =>
                        ((option?.label as string) ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase()) ||
                        ((option?.value as string) ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase()) ||
                        option?.value === DIRECT_INPUT_VALUE,
                      dropdownRender: (menu) => {
                        return (
                          <CustomDropdownWrapper>{menu}</CustomDropdownWrapper>
                        );
                      },
                      onChange: (label: any, option: any) => {
                        if (option) {
                          setValue("freetime", option.label);
                        }
                      },
                      onClear: () => {
                        setValue("freetime", undefined);
                      },
                    }}
                    bottomAccessory={
                      watch("freetimeCodeItemName") === DIRECT_INPUT_VALUE ? (
                        <Input
                          {...register("freetimeDirect")}
                          placeholder={t("placeholder:enterFreetime")}
                          onClear={() => setValue("freetimeDirect", "")}
                        />
                      ) : null
                    }
                  />
                )}
              </HalfFormItemContainer>
              <HalfFormItemContainer>
                {!isOriginByExport && (
                  <StyledFormItem
                    label={t("contract:importer.add.common.origin")}
                    control={control as any}
                    name="origin"
                    type="text"
                    inputProps={{
                      placeholder: t(
                        "contract:importer.add.placeholder.origin"
                      ),
                    }}
                  />
                )}
              </HalfFormItemContainer>
            </FlexRow>
          </FlexColumn>
        </SectionCard>
      </>
    );
  };

  // 첨부 파일
  const renderAttachmentFile = () => {
    return (
      <StyledSectionCard
        cardTitle={t("contract:importer.add.common.attachmentFile")}
      >
        <FlexGapDiv>
          <StyledFormItem
            label={t("contract:importer.add.common.poFile")}
            type="file"
            name="poMediaId"
            control={control as any}
            inputProps={{
              defaultFileList: watch("poMediaId")
                ? [
                    new File(
                      [""],
                      watch("poMediaId")?.[0]?.originalFileName ?? ""
                    ),
                  ]
                : undefined,
              onRemove: () => setValue("poMediaId", undefined),
            }}
          />
          <StyledFormItem
            label={t("contract:importer.add.common.referenceImage")}
            type="imageUpload"
            name="referenceMediaIdList"
            control={control as any}
            errorsMessage={{
              required: t("error:required"),
            }}
            bottomAccessory={
              <WarningDescription>
                <StyledIcon iconSrc={InfoSvg} iconSize={16} />
                <Typo typoType="b9r" color="gray6">
                  {t("contract:importer.add.placeholder.referenceImage")}
                </Typo>
              </WarningDescription>
            }
            inputProps={{
              imageSrcList: watch("referenceMediaIdList")?.map(
                ({ mediaUrl }) => {
                  return {
                    imageSrc: mediaUrl,
                    onClick: () => handleImageClick(mediaUrl),
                  };
                }
              ),
              onRemove: (removeItemUrl) => {
                setValue(
                  "referenceMediaIdList",
                  watch("referenceMediaIdList").filter(
                    ({ mediaUrl }) => mediaUrl !== removeItemUrl
                  )
                );
              },
            }}
          />
        </FlexGapDiv>
      </StyledSectionCard>
    );
  };

  // Remark
  const renderContractRemark = () => {
    return (
      <StyledSectionCard
        cardTitle={t("contract:importer.add.common.contractRemark")}
      >
        <FormItem
          type="textarea"
          name="importerContractRemark"
          control={control}
          inputProps={{
            style: { width: "100%" },
            placeholder: t("contract:importer.add.placeholder.contractRemark"),
            maxLength: 1000,
          }}
          noLabel
        />
      </StyledSectionCard>
    );
  };

  const renderDialog = () => {
    if (dialogState === DialogState.IMAGE && selectImage) {
      return (
        <ReferenceImageDialog
          open
          onOpenChange={() => setDialogState(DialogState.NULL)}
          imageList={referenceImageList ?? [""]}
          selectImage={selectImage}
          onChangeSelect={setSelectImage}
        />
      );
    }

    // 아이템 추가
    if (dialogState === DialogState.ADD_ITEM) {
      return (
        <AddItemDialog
          open
          onOpenChange={() => setDialogState(DialogState.NULL)}
          isSingleItemCode
          onSave={handleAddItems}
          allowedMainCategories={buyerMainCategory}
        />
      );
    }
    // 거래처 추가
    if (dialogState === DialogState.ADD_CONTACT_PERSON) {
      const selectedBuyer = buyerList.find(
        ({ id }) => watch("importerBuyerId") === id.toString()
      );

      return (
        <BuyerEmployeeAddDialog
          onSave={handleAddBuyerEmployee}
          onClose={() => setDialogState(DialogState.NULL)}
          defaultCountryCode={selectedBuyer?.telPrefix}
        />
      );
    }
    // 약관추가
    if (dialogState === DialogState.CREATE_TERMS) {
      return (
        <TermsAddDialog
          title={t("common:registerOrderTerms")}
          open
          onOpenChange={() => setDialogState(DialogState.NULL)}
          onSave={async () => await exportersData.refetch()}
        />
      );
    }
    // 서명추가
    if (dialogState === DialogState.REGISTER_SIGN) {
      return (
        <RegisterCompanySignDialog
          onOpenChange={() => setDialogState(DialogState.NULL)}
        />
      );
    }
  };

  const renderAlertDialog = () => {
    const closeAlertDialog = () => setAlertDialogState(AlertDialogState.NULL);
    // 뒤로가기
    if (alertDialogState === AlertDialogState.BACK_TO_PREVIOUS) {
      return (
        <AlertDialog
          open
          title={t("contract:importer.add.alertDialog.backToPrevious.title")}
          onOpenChange={() => {
            closeAlertDialog();
          }}
          onOk={() => navigate(-1)}
          okText={t("contract:importer.add.common.ok")}
          cancelText={t("contract:importer.add.common.exit")}
        >
          {t("contract:importer.add.alertDialog.backToPrevious.description")}
        </AlertDialog>
      );
    }
    // 서명등록
    if (alertDialogState === AlertDialogState.REGISTER_SIGN) {
      return (
        <NoRegisteredSignatureAlertDialog
          open
          onOpenChange={() => closeAlertDialog()}
          onOk={() => {
            setDialogState(DialogState.REGISTER_SIGN);
            closeAlertDialog();
          }}
          onCancel={closeAlertDialog}
        />
      );
    }
    // 서명등록 요청
    if (alertDialogState === AlertDialogState.SIGNATURE_REQUEST) {
      return (
        <SignatureRequestAlertDialog
          open
          onOpenChange={() => closeAlertDialog()}
          onCancel={closeAlertDialog}
        />
      );
    }
    // SC서명선택 (서명 2개일 경우)
    if (alertDialogState === AlertDialogState.SIGNATURE_SELECT_PO) {
      return (
        <SignatureSelectDialog
          open
          onSave={(selectSignatureMediaId) => {
            createPoContract(getValues(), selectSignatureMediaId);
            closeAlertDialog();
          }}
          onOpenChange={closeAlertDialog}
        />
      );
    }
  };

  useEffect(() => {
    (async () => {
      try {
        handleContentLoadingOn();
        const result = await getContractDetail({ id });

        if (result.isSuccess) {
          const importerBuyerId = result.data.importerBuyerId;

          reset({
            importerBuyerId: importerBuyerId.toString(),
            poNo: result.data.poNo,
            hsCode: result.data.hsCode,
            lastShipmentDateAt: result.data.lastShipmentDateAt
              ? dayjs(result.data.lastShipmentDateAt)
              : undefined,
            freetime: result.data.freetime || undefined,
            freetimeCodeItemName:
              !result.data.freetimeCodeItemName && !!result.data.freetime
                ? DIRECT_INPUT_VALUE
                : result.data.freetimeCodeItemName,
            freetimeDirect:
              !result.data.freetimeCodeItemName && !!result.data.freetime
                ? result.data.freetime
                : undefined,
            referenceMediaIdList: result.data.referenceSimpleMedias ?? [],
            importerOrderDateAt: result.data.importerOrderDateAt
              ? dayjs(result.data.importerOrderDateAt)
              : dayjs(),
          });

          await getBuyerEmployees({ id: importerBuyerId });
        }
      } catch (e: any) {
        const message = Array.isArray(e.data.message)
          ? e.date.message[0]
          : e.date.message;
        alert.showAlert({ message, type: "error" });
      } finally {
        handleContentLoadingOff();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ImporterMainLayout
      breadcrumb={[t("sideNav:importContract"), t("sideNav:createPO")]}
      customPageTitle={
        <HeaderInnerDiv>
          <h1>{t("sideNav:importContractRegistration")}</h1>
        </HeaderInnerDiv>
      }
      pageTitle={t("sideNav:importContractRegistration")}
    >
      <ContentSection>
        <StyledForm onSubmit={handleSubmit(handleCreateContract)}>
          <WarningDescription>
            <Icon iconSrc={InfoSvg} iconSize={16} />
            {t("contract:importer.add.contractAddInfo")}
          </WarningDescription>

          {renderContractInfoBasedPo()}
          {renderAdditionalInfoBasedPo()}
          <Section>
            {renderAttachmentFile()}
            {renderContractRemark()}
          </Section>

          <BottomFixedContainer>
            <FooterButtonSection>
              <Button
                buttonGrade="tertiary"
                buttonColor="black"
                style={{ display: "flex", alignItems: "center", gap: "4px" }}
                onClick={() =>
                  setAlertDialogState(AlertDialogState.BACK_TO_PREVIOUS)
                }
              >
                <Icon iconSrc={ChevronLeftSvg} iconSize={16} />{" "}
                {t("contract:importer.add.buttonLabel.backToPrevious")}
              </Button>

              <SaveButtonContainer>
                <Button
                    ref={previewButtonRef}
                    onClick={async () => {
                      previewPoPdfResourceRef.current = getValues();
                      setIsPreviewLoading(true);
                      try {
                        await handlePreview();
                      } catch (error) {
                        console.error("PDF 미리보기 실패:", error);
                      } finally {
                        setIsPreviewLoading(false);
                      }
                    }}
                    buttonGrade="tertiary"
                    buttonColor="black"
                    isLoading={exportersData.isFetching || isPreviewLoading}
                    disabled={exportersData.isFetching || isPreviewLoading}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <Icon iconSrc={PreviewSvg} iconSize={16} />
                    {t("contract:exporter.add.buttonLabel.preview")}
                  </Button>

                <Button
                  buttonGrade="primary"
                  style={{ width: 158 }}
                  type="submit"
                >
                  {t("contract:importer.add.buttonLabel.save")}
                </Button>
              </SaveButtonContainer>
            </FooterButtonSection>
          </BottomFixedContainer>
        </StyledForm>
      </ContentSection>

      {renderDialog()}
      {renderAlertDialog()}
    </ImporterMainLayout>
  );
};

export default ContractAddBasedOnScPage;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SaveButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  text-align: center;
`;

const StyleButton = styled(Button)`
  ${typo.btn3m}
`;

const HeaderInnerDiv = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 0;

  h1 {
    ${typo.h1}
  }

  ${StyleButton} {
    flex-shrink: 0;
    margin-left: auto;
  }
`;

const WarningDescription = styled.p`
  ${typo.b9r};
  color: ${colorSet.gray4};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const HalfFormItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: calc(50% - 12px);
  gap: 24px;
`;

const SubFormLabel = styled.p`
  color: ${colorSet.gray6};
  ${typo.b9r};

  strong {
    color: ${colorSet.red2};
    font-size: 12px;
    padding-left: 2px;
  }
`;

const FlexDiv = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  &.selector-size-s {
    width: calc(100% - 164px - 16px - 100px);
  }
`;

const SubFormRow = styled.div<{ isRequired?: boolean }>`
  display: flex;
  width: 100%;
  gap: 8px;

  ${FlexDiv} {
    flex: 1;
  }

  ${SubFormLabel} {
    width: 100px;
    flex-shrink: 0;
  }
`;

const StyledSelect = styled(Select)`
  width: 100%;

  &[data-invalid="true"] {
    .ant-select-selector {
      border: 1px solid ${colorSet.red2} !important;
    }
  }

  .ant-select-selection-placeholder {
    width: 1px;
  }
`;

const CustomLabel = styled.p`
  width: 164px;
  color: ${colorSet.gray6};
  ${typo.b9r};
  flex-shrink: 0;

  &::after {
    content: " *";
    color: ${colorSet.red2};
  }
`;

const StyledCustomLabel = styled(CustomLabel)`
  color: ${colorSet.gray1};
  ${typo.b7r};
`;

const CustomFormItemRow = styled.div`
  display: flex;
  gap: 8px;

  ${mediaQuery.FORM_INPUT_TO_FULL_INPUT} {
    flex-direction: column;
  }
`;

const DoubleInputContainer = styled.div`
  display: flex;
  gap: 8px;
  flex: 1;

  &.fixed-width-container {
    width: calc(100% - 172px);

    ${mediaQuery.FORM_INPUT_TO_FULL_INPUT} {
      width: 100%;
    }
  }
`;

const StyledSectionCard = styled(SectionCard)`
  flex: 1;
`;

const Section = styled.section`
  display: flex;
  gap: 16px;
`;

const FlexGapDiv = styled(FlexDiv)`
  gap: 24px;
`;

const ContentSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 72px;
`;

const StyledInputError = styled(InputError)`
  padding-top: 8px;
`;

const DoubleInputContainerWithError = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FooterButtonSection = styled.section`
  display: flex;
  justify-content: space-between;
`;

const StyledFormItem = styled(FormItem)`
  .ant-select-selection-placeholder {
    width: 1px;
  }

  .selector-size-m {
    width: calc(100% - 164px - 8px);
  }

  .ant-picker .ant-picker-clear {
    width: 18px;
    height: 18px;
    outline: 1px solid white;
  }

  ${mediaQuery.FORM_INPUT_TO_FULL_INPUT} {
    flex-direction: column;
    gap: 8px;

    .selector-size-m {
      width: 100%;
    }
  }
`;

const StyledIcon = styled(Icon)`
  flex-shrink: 0;
`;

const AddIcon = styled(AddSvg)<{ disabled?: boolean }>`
  width: 16px;
  height: 16px;

  path {
    ${({ disabled }) =>
      disabled &&
      css`
        fill: ${colorSet.gray8};
      `};
  }
`;

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  flex-shrink: 0;
`;

const CustomDropdownWrapper = styled.div`
  div.ant-select-item {
    &:first-of-type {
      color: ${colorSet.blue4};

      &::before {
        content: url(${AddBlueSvg});
        width: 18px;
        height: 18px;
        position: relative;
        margin-right: 8px;
        top: 4px;
      }
    }
  }
`;

const StyledSectionCardRow = styled(SectionCardRow)`
  &[data-columns="3"] {
    grid-column: 3 span;
  }

  &[data-columns="4"] {
    grid-column: 4 span;
  }

  &[data-columns="6"] {
    grid-column: 6 span;
  }

  &[data-columns="12"] {
    grid-column: 12 span;
  }

  &[data-divider="true"] {
    border-top: 1px solid ${colorSet.gray9};
  }
`;

const BuyerText = styled(Typo)`
  text-decoration-line: underline;
  text-underline-position: under;
  text-decoration-thickness: 2px;
  cursor: pointer;
  word-break: break-all;
`;

const BottomSectionCard = styled(SectionCardGrid)`
  padding: 16px 24px;
  background: ${colorSet.gray11};
  border: 1px solid ${colorSet.gray10};
  text-align: center;
`;

const StyledSectionCardCenterRow = styled(StyledSectionCardRow)`
  align-items: center;

  .ant-typography {
    word-break: break-all;
    white-space: pre-wrap;
  }
`;

const FlexColumn = styled.div<{ flexGap?: number }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${({ flexGap }) =>
    flexGap &&
    css`
      gap: ${flexGap}px;
    `};
`;

const FlexRow = styled.div`
  display: flex;
  width: 100%;
  gap: 24px;
`;

const Divider = styled.div`
  border-top: 1px solid ${colorSet.gray9};
  margin: 24px 0;
`;

const DirectInputSelectOptionButton = styled(SelectOptionButton)`
  color: ${colorSet.blue4};

  &:hover {
    &&& {
      color: ${colorSet.blue4};
    }
  }

  &::before {
    content: url(${AddBlueSvg});
    width: 18px;
    height: 18px;
    position: relative;
    margin-right: 8px;
    top: 4px;
  }
`;
